import { inject, Service } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MngBaseDialogComponent } from './mng-base-dialog.component';
import {
  BaseDialogData,
  DIALOG_RESULT_CANCEL,
  DIALOG_RESULT_NO,
  DIALOG_RESULT_YES,
  DialogType,
  IDialogOptions
} from './mng-dialog.types';
import { DomSanitizer } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';

// TODO: warning/info/error dialog

@Service()
export class MngDialogService {
  dialog = inject(MatDialog);
  sanitizer = inject(DomSanitizer);

  showOK(options: IDialogOptions, okFunction?: () => unknown): MatDialogRef<MngBaseDialogComponent> {
    options.type = DialogType.Ok;

    const dialogRef: MatDialogRef<MngBaseDialogComponent> = this.open(options);
    if (okFunction) {
      dialogRef.afterClosed().subscribe(() => {
        okFunction();
      });
    }
    return dialogRef;
  }

  showOkSimple(htmlContent: string): MatDialogRef<MngBaseDialogComponent> {
    return this.showOK({ htmlContent });
  }

  showYesNo(
    options: IDialogOptions,
    yesFunction?: () => unknown,
    noFunction?: () => unknown
  ): MatDialogRef<MngBaseDialogComponent> {
    options.type = DialogType.YesNo;
    options.cancelButton = false;
    const dialogRef: MatDialogRef<MngBaseDialogComponent> = this.open(options);
    if (yesFunction || noFunction) {
      void firstValueFrom(dialogRef.afterClosed()).then((answer) => {
        if (yesFunction && DIALOG_RESULT_YES === answer) {
          yesFunction();
        }
        if (noFunction && DIALOG_RESULT_NO === answer) {
          noFunction();
        }
      });
    }
    return dialogRef;
  }

  async showYesNoWithYesResult(options: IDialogOptions, yesFunction?: () => unknown, noFunction?: () => unknown) {
    let result: unknown;
    await firstValueFrom(this.showYesNo(options, () => (result = yesFunction()), noFunction).afterClosed());
    return result;
  }

  showTextInput(
    options: IDialogOptions,
    textHandlerFunction?: (text: string) => void
  ): MatDialogRef<MngBaseDialogComponent> {
    options.type = DialogType.TextInput;
    const dialogRef: MatDialogRef<MngBaseDialogComponent> = this.open(options);
    if (textHandlerFunction) {
      void firstValueFrom(dialogRef.afterClosed()).then((result) =>
        textHandlerFunction(DIALOG_RESULT_CANCEL !== result ? result : undefined)
      );
    }
    return dialogRef;
  }

  showDateInput(options: IDialogOptions): MatDialogRef<MngBaseDialogComponent> {
    options.type = DialogType.TextInputAsDate;
    return this.open(options);
  }

  showModal(options: IDialogOptions): MatDialogRef<MngBaseDialogComponent> {
    return this.open(options);
  }

  open(options: IDialogOptions): MatDialogRef<MngBaseDialogComponent> {
    const bodyHtml = options.htmlContent ? this.sanitizer.bypassSecurityTrustHtml(options.htmlContent) : null;

    const data: BaseDialogData = {
      title: options.title || 'Achtung',
      iconName: options.iconName,
      iconColor: options.iconColor,
      bodyHtml,
      dialogType: options.type || DialogType.YesNo,
      autoselect: options.autoselect || false,
      cancelEnabled: options.cancelButton !== undefined ? options.cancelButton : true,
      customValidator: options.customTextInputValidator
    };

    if (options.yesText) {
      data.yesText = options.yesText;
    }

    if (options.noText) {
      data.noText = options.noText;
    }

    if (options.okText) {
      data.okText = options.okText;
    }

    if (options.cancelText) {
      data.cancelText = options.cancelText;
    }

    if (options.type === DialogType.TextInputAsDate) {
      data.minDate = options.minDate;
      data.maxDate = options.maxDate;
    }

    const dialogRef = this.dialog.open(MngBaseDialogComponent, {
      width: options.width || '480px',
      disableClose: options.cancelButton !== undefined ? !options.cancelButton : false,
      data: data
    });

    if (options.type === DialogType.TextInput || options.type === DialogType.TextInputAsDate) {
      const validators = options.customTextInputValidator
        ? [Validators.required, options.customTextInputValidator.validatorFunction]
        : [Validators.required];
      dialogRef.componentInstance.formControl = new FormControl('', { validators: validators });
      if (options.textInput) {
        dialogRef.componentInstance.formControl.setValue(options.textInput);
      }
    }

    return dialogRef;
  }
}
